import { useCallback } from "react";

import { notify } from "../../../../components/Notification";
import { ErrorResponse } from "../../../../services";
import {
  InvestmentMarketUpdateItem,
  InvestmentTransferItem,
} from "../../../../services/investments/types";
import { TransactionTypeOptions } from "../../../Transactions";
import { Progress } from "../../panels";
import {
  UseLegacyProgressPanelsProps,
  UseLegacyProgressPanelsReturn,
} from "./types";
import { useTransactionDetails } from "../../../Transactions/hooks";

// The pre-ledger contribution and market-update rows, which open the same
// progress panel a manual investment writes through.
export const useLegacyProgressPanels = ({
  openEditPanel,
  openDuplicatePanel,
}: UseLegacyProgressPanelsProps): UseLegacyProgressPanelsReturn => {
  const fetchTransactionDetails = useTransactionDetails();

  // A contribution row carries summary data only, so the full transaction has
  // to be read before the panel can be prefilled from it.
  const buildProgressFromContribution = useCallback(
    async (item: InvestmentTransferItem): Promise<Progress> => {
      const details = await fetchTransactionDetails(item.id);
      const transactionType =
        item.direction === "in"
          ? TransactionTypeOptions.TRANSFER_IN
          : TransactionTypeOptions.TRANSFER_OUT;
      return {
        id: details.id,
        amount: String(Math.abs(details.amount)),
        transactionDate: new Date(details.transactionDate),
        transactionType,
        fromAccountId: details.fromAccountId,
        fromAccountName: details.fromAccountName ?? null,
        toAccountId: details.toAccountId,
        toAccountName: details.toAccountName ?? null,
        note: details.note,
      };
    },
    [fetchTransactionDetails],
  );

  const openContributionPanel = useCallback(
    async (item: InvestmentTransferItem, mode: "edit" | "duplicate") => {
      try {
        const progress = await buildProgressFromContribution(item);
        if (mode === "edit") {
          openEditPanel(progress);
        } else {
          openDuplicatePanel(progress);
        }
      } catch (err: unknown) {
        notify.apiError(err as ErrorResponse);
      }
    },
    [buildProgressFromContribution, openEditPanel, openDuplicatePanel],
  );

  // A market update row already carries the value and date, so the progress
  // panel can be built directly without an extra fetch.
  const openMarketUpdatePanel = useCallback(
    (item: InvestmentMarketUpdateItem, mode: "edit" | "duplicate") => {
      const progress: Progress = {
        id: item.id,
        amount: String(item.value),
        transactionDate: new Date(item.date),
        transactionType: TransactionTypeOptions.MARKET_UPDATE,
        fromAccountId: null,
        fromAccountName: null,
        toAccountId: null,
        toAccountName: null,
      };
      if (mode === "edit") {
        openEditPanel(progress);
      } else {
        openDuplicatePanel(progress);
      }
    },
    [openEditPanel, openDuplicatePanel],
  );

  return { openContributionPanel, openMarketUpdatePanel };
};
